import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardCheck, Compass, Bell, Download, Share, X } from "lucide-react";
import { isStandalone, getPlatform } from "@/lib/pwa";

const SEEN_KEY = "a2g_welcome_seen";

const STEPS = [
  [ClipboardCheck, "Fai il test", "18 domande, meno di due minuti: scopri il tuo archetipo."],
  [Compass, "Sblocca il percorso", "Attiva la prova gratuita di 7 giorni e apri il tuo percorso guidato."],
  [Bell, "Usa l'app ogni giorno", "Tre momenti al giorno con un promemoria gentile."],
];

export function WelcomeOverlay() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const platform = getPlatform();

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(SEEN_KEY) === "1") return;
    setOpen(true);
  }, []);

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const go = (path) => {
    close();
    navigate(path);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
          data-testid="welcome-overlay"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[430px] rounded-t-3xl border border-[#D4AF37]/25 bg-gradient-to-b from-[#141416] to-[#0A0A0C] p-6 pb-8 sm:rounded-3xl"
          >
            <button onClick={close} data-testid="welcome-close" className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#8A8E96]">
              <X size={16} />
            </button>

            <img src="/logo-horizontal.png" alt="Coach2Go" className="h-7 w-auto object-contain" />
            <p className="eyebrow mt-5">Ti diamo il benvenuto</p>
            <h2 className="font-serif-el mt-2 text-3xl font-semibold leading-tight text-[#FAF9F6]">Il tuo COACH sempre in tasca</h2>
            <p className="mt-2 text-base leading-relaxed text-[#B4B4BC]">Bastano tre passi per iniziare a trasformare le intenzioni in azioni.</p>

            <div className="mt-5 space-y-2.5">
              {STEPS.map(([Icon, t, d], i) => (
                <div key={t} className="flex gap-3 rounded-2xl border border-[#27272A] bg-[#121215] p-3.5">
                  <span className="tabular-nums font-semibold flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#E5C158] to-[#997A20] text-sm font-bold text-[#09090B]">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="flex items-center gap-1.5 text-base text-[#F4F4F5] font-semibold"><Icon size={14} className="text-[#D4AF37]" /> {t}</h3>
                    <p className="mt-0.5 text-sm leading-snug text-[#B4B4BC]">{d}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <button onClick={() => go("/installa")} data-testid="welcome-install-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide">
                <Download size={18} /> Scarica l'app sul telefono
              </button>
              <button onClick={close} data-testid="welcome-skip-button" className="w-full text-center text-sm font-medium text-[#8A8E96]">
                Salta per ora
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
